import { Table, Button, Select, Spin, Alert, Modal, Tabs, message } from "antd";
import React, { useState, useEffect, useMemo } from "react";
import { getTenantId } from "../../../../utils/auth";
import axiosInstance from "../../../../utils/axiosInstance";
import LineChart, { LineData } from "../../../../components/LineChart";
import LineChartWithTitle from "../../../../components/LineChartWithTitle";

import { motion } from "framer-motion";

import {
  GEO_LIFT_MARKET_SELECTION,
  GEO_LIFT_MARKET_SELECTION_SINGLE_SCM,
  GEO_LIFT_POWER_PLOT_API,
} from "../../../../constants/API";
import { useNavigate, useSearchParams } from "react-router-dom";
import MarketAnalysisDashboard from "../../../../components/MarketAnalysisDashboard";
import { findMatchingLocation } from "../../../../utils/locationMatching";
import { capitalizeLocation } from "../../../../utils/utils";
import EnhancedLoadingIndicator from "../../../../components/EnhancedLoadingIndicator";

interface MarketData {
  Holdout: number;
  ID: number;
  location: string;
  duration: number;
  EffectSize: number;
  Power: number;
  AvgScaledL2Imbalance: number;
  Investment: number;
  AvgATT: number;
  Average_MDE: number;
  ProportionTotal_Y: number;
  rank: number;
  abs_lift_in_perc: number;
  correlation: number;
}

interface MarketDataNew {
  location: string;
  Investment: number;
  duration: number;
  Power: number;
  abs_lift_in_perc: number;
  PercentInvestment: number;
  EstimatedInvestment: number;
}

interface ViewTestAndControlMarketProps {
  testMarkets: string[];
  selectedMarket: string;
  marketData: MarketData[];
  jobConfigId: string;
  mode: any;
  buyType: any;
  preCampaignData: {
    geoLiftStudyName: string;
    buyType: "national" | "non-national";
    fileList: File[];
  };
  onMarketChange: (value: string) => void;
  onTestMarketsChange?: (markets: string[]) => void;
  refreshTrigger?: number;
  isSales: boolean
}

const ViewTestAndControlMarket: React.FC<ViewTestAndControlMarketProps> = ({
  testMarkets,
  selectedMarket,
  marketData,
  preCampaignData,
  jobConfigId,
  onMarketChange,
  onTestMarketsChange,
  mode,
  buyType,
  refreshTrigger,
  isSales
}) => {
  const [searchParams] = useSearchParams();
  console.log("marketData",marketData);
  const formatLocation = (location?: string) => {
    if (!location) return "-";
    return location
      .replace(/[()'-]/g, "")
      .replace(/multioutletc/gi, "")
      .trim()
      .split(",")
      .map((loc) => loc.trim().replace(/\b\w/g, (char) => char.toUpperCase()))
      .join(", ");
  };
  const [singleScmMarketData, setSingleScmMarketData] = useState<
    MarketDataNew[] | []
  >([]);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [geoLiftObsLabel, setGeoLiftObsLabel] = useState<
    string[] | undefined
  >();
  const [geoLiftPowerLabel, setGeoLiftPowerLabel] = useState<
    string[] | undefined
  >();

  const [geoLiftPowerInvestmentLabel, setGeoLiftPowerInvestmentLabel] = useState<
    string[] | undefined
  >();


  const [plotDuration, setPlotDuration] = useState<number | undefined>();
  const [treatmentValue, setTreatmentValue] = useState<
    { name: string; data: number[] }[] | undefined
  >();
  const [treatmentPowerValue, setTreatmentPowerValue] = useState<
    { name: string; data: number[] }[] | undefined
  >();

  const [treatmentPowerInvestmentValue, setTreatmentPowerInvestmentValue] = useState<
    { name: string; data: number[] }[] | undefined
  >();

  const [chartKey, setChartKey] = useState<number>(0);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [selectedTitle, setSelectedTitle] = useState<string>("");
  const [selectedTreatmentControlTitle, setSelectedTreatmentControlTitle] = useState<string>("");

  const [selectedPowerTitle, setSelectedPowerTitle] = useState<string>("");

  const [selectedPowerInvestmentTitle, setSelectedPowerInvestmentTitle] = useState<string>("");


  const [buttonLoading, setButtonLoading] = useState(false);
  const [locationData, setLocationData] = useState<any[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [powerWellPlotData, setPowerWellPlotData] = useState<any>();
  const [loading, setLoading] = useState(true);

  const handleButtonClick = async (marketName: string) => {
    if (!marketData || marketData.length === 0) {
      return;
    }
    setIsModalVisible(true);
    setSelectedTitle("Loading...");
    setButtonLoading(true);
    try {
      await handleViewControlMarket(
        marketData[0],
        jobConfigId,
        marketName.toLowerCase()
      );
    } catch (error) {
      console.log('error in handleViewControlMarket',error);
      message.error("Failed to load control market data. Please try again.");
      setIsModalVisible(false);
      setSelectedTitle("");
    } finally {
      setButtonLoading(false);
    }
  };

  useEffect(() => {
    if (isModalVisible) {
      setTimeout(() => setChartKey(Date.now()), 300);
    }
  }, [isModalVisible]);

  useEffect(() => {
    setSelectedLocation(selectedMarket);
  }, [selectedMarket]);

  const fetchAllLocationsWithLatLong = async () => {
    if (!jobConfigId) {
      return;
    }
    setLoadingLocations(true);
    try {
      const response = await axiosInstance.get(
        `/model/geo_lift/getLatLong/${jobConfigId}`
      );
      if (response.data?.success && response.data?.data) {
        setLocationData(response.data.data);
        if (response.data.warning) {
          message.warning(response.data.warning);
        }
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
      } else {
        message.error("Failed to fetch location data");
      }
    } finally {
      setLoadingLocations(false);
    }
  };

  useEffect(() => {
    if (jobConfigId) {
      fetchAllLocationsWithLatLong();
    }
  }, [jobConfigId]);

  const handleViewControlMarket = async (
    record: any,
    jobConfigId: string,
    marketName: string
  ) => {
    const marketSelectionPayload = {
      marketSelectionId: record.ID,
      marketName: marketName,
      jobConfigId: jobConfigId,
      jobConfig: jobConfigId,
      tenantId: getTenantId(),
    };
    const marketSelectionPromise = axiosInstance.post(
      GEO_LIFT_MARKET_SELECTION,
      marketSelectionPayload,
      {
        timeout: 300000,
      }
    );
    setTreatmentValue([]);
    const powerPlotPromise = axiosInstance.post(
      GEO_LIFT_POWER_PLOT_API,
      marketSelectionPayload,
      {
        timeout: 300000,
      }
    );
    return Promise.allSettled([marketSelectionPromise, powerPlotPromise])
      .then(([marketSelectionResult, powerPlotResult]) => {
        if (marketSelectionResult.status === "rejected") {
          throw new Error("Failed to load market selection data");
        }
        let marketData = marketSelectionResult.value.data;
        if (typeof marketData === "string") {
          try {
            marketData = JSON.parse(marketData);
          } catch (error) {}
        }

        if (marketData) {
          const geoLiftPlot = marketData.lift_plot;

          const labels = Object.values(
            geoLiftPlot?.date ? geoLiftPlot?.date : geoLiftPlot?.Time
          ).map((time: any) => time.toString());
          setGeoLiftObsLabel(labels);
          const c_obsData = Object.values(geoLiftPlot.c_obs);
          const t_obsData = Object.values(geoLiftPlot.t_obs);
          console.log("duration: c_obsData", record?.duration);

          setPlotDuration((t_obsData.length - record?.duration) as any);


          const dataSets = [
            {
              name: `Test Location (${marketName})`,
              data: t_obsData,
              borderColor: "#006400",
              backgroundColor: "rgba(59, 2, 246, 0.0)",
              textColor: "blue",
            },
            {
              lineType: "dashed",
              name: "Synthetic Control",
              data: c_obsData,
              borderColor: "#d6279a",
              backgroundColor: "rgba(59, 130, 246, 0.0)",
              textColor: "rgba(128, 128, 128, 1)",
            },
          ];

          setTreatmentValue(dataSets as any);
          setChartKey((prev) => prev + 1);

          setSelectedTitle('Power Analysis');
          setSelectedTreatmentControlTitle('Treatment vs Control Graph');

          //Combine power_curves directly here



        }
        if (powerPlotResult.status === "fulfilled") {
          let powerData = powerPlotResult.value.data;
          if (typeof powerData === "string") {
            try {
              powerData = JSON.parse(powerData);
            } catch (error) {
              console.error(
                "Failed to parse power plot response as JSON:",
                error
              );
            }
          }

        if (powerData) {

        const labels = powerData.EffectSize.map((EffectSize:any)=> EffectSize.toString());
        const investmentLabels = powerData.Investment.map((Investment:any) =>
          `${isSales ? "$" : ""}${Investment.toLocaleString('en-US', {maximumFractionDigits: 0})}`
        );
        //const labels = effectLabels.map((effectLabel:any,index:any)=>{return effectLabel+","+investmentLabels[index]});

        //console.log("effectLabels",effectLabels);
        //console.log("investmentLabels",investmentLabels);
        //console.log("labels",labels);

        //console.log("isSales is",isSales);

        setGeoLiftPowerLabel(labels);

        if (isSales==true)
        {

          console.log("isSales",isSales);
          console.log("type")
          setGeoLiftPowerInvestmentLabel(investmentLabels);
          console.log("investmentLabels",investmentLabels);
          console.log("effect labels",labels);
          const incrementalSales = Object.values(powerData.IncrementalSales);

          const dataSetsInvestments = [
            {
              name: `Incremental Sales (${marketName})`,
              data: incrementalSales,
              borderColor: "#006400",
              backgroundColor: "rgba(59, 2, 246, 0.0)",
              textColor: "blue",
            }

          ];

        setTreatmentPowerInvestmentValue(dataSetsInvestments as any);
        setSelectedPowerInvestmentTitle('Investment Graph');
        }

        const power = Object.values(powerData.power);
        console.log("power",power);

        //setPlotDuration((power.length) as any);

        const dataSets = [
            {
              name: `Power (${marketName})`,
              data: power,
              borderColor: "#006400",
              backgroundColor: "rgba(59, 2, 246, 0.0)",
              textColor: "blue",
            }

          ];


          setTreatmentPowerValue(dataSets as any);
          setChartKey((prev) => prev + 1);

          setSelectedPowerTitle('Power Graph');




          //setSelectedTitle(`Power Incremental Sales Graph`);







          // Update power well plot data with the current selected location
          //const updatedPowerWellPlot = {
          //  ...powerData,
          //  PowerCurves: powerData.PowerCurves?.map((curve: any) => ({
          //    ...curve,
          //    location: selectedLocation, // Update location to match selected market
          //  })),
          //};
          //setPowerWellPlotData(updatedPowerWellPlot);

          //setLoading(false);
        }


        else
        {
          console.warn(
            "Power plot API failed, but continuing with market selection data:"
          );

        }
      }}).catch((e) => {
        throw e;
      });
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };


  const marketSelection = async () => {
    if (!jobConfigId) {
      console.warn(
        "No jobConfigId provided, skipping market selection API call"
      );
      return;
    }

    setLoadingLocations(true);

    // Poll for data with retries (backend processing takes ~15 minutes)
    const maxRetries = 20; // 20 attempts = 20 minutes max (giving some buffer)
    const retryDelay = 60000; // 1 minute (60 seconds) between attempts

    const attemptFetch = async (attemptNumber: number): Promise<void> => {
      try {
        console.log(
          `Attempt ${attemptNumber}/${maxRetries}: Fetching market selection data for jobConfigId: ${jobConfigId}`
        );
        const response = await axiosInstance.post(
          `${GEO_LIFT_MARKET_SELECTION_SINGLE_SCM}/${jobConfigId}`,
          {
            job_config_id: jobConfigId,
            total_investment: 20000,
          }
        );

        const transformedData: MarketDataNew[] = response.data.map(
          (item: any) => ({
            location: capitalizeLocation(item.location[0]),
            Investment: `$ ${Number(item.Investment[0]).toFixed(0)}`,
            EstimatedInvestment: `$ ${Number(
              item.EstimatedInvestment[0]
            ).toFixed(0)}`,
            duration: `${item.duration[0]} Weeks`,
            Power: item.Power[0],
            abs_lift_in_perc: `${item.abs_lift_in_perc[0]} %`,
            PercentInvestment: `${Number(item.PercentInvestment[0]).toFixed(
              2
            )} %`,
            IncrementalSales: `${isSales ? "$ " : ""}${(Number(item.IncrementalSales[0]) || 0).toLocaleString('en-US', {maximumFractionDigits: 0})}`


          })
        );

        //console.log("transformedData",transformedData);
        setSingleScmMarketData(transformedData);
        setLoadingLocations(false);
      } catch (err: any) {
        console.warn(
          `Attempt ${attemptNumber} failed:`,
          err.response?.status,
          err.message
        );

        // If max retries reached, show error and stop
        if (attemptNumber >= maxRetries) {
          console.error(
            "Max retries reached. Backend processing may have failed."
          );
          message.error(
            "Data processing is taking longer than expected. Please refresh the page or check the dashboard."
          );
          setLoadingLocations(false);
          return;
        }

        setTimeout(() => attemptFetch(attemptNumber + 1), retryDelay);
      }
    };

    // Start polling
    attemptFetch(1);
  };

  useEffect(() => {
    if (jobConfigId) {
      // Check if this is a new job or edit/view mode
      const modeParam = searchParams.get("mode");
      const editParam = searchParams.get("edit");
      const isNewJob = !!modeParam && editParam !== "true"; // New job has mode but NOT edit=true

      if (isNewJob) {
        // Show loading indicator for new jobs
        setLoadingLocations(true);
        return;
      }

      // If refreshTrigger > 0, it means job was just updated, show loading
      if (refreshTrigger && refreshTrigger > 0) {
        setLoadingLocations(true);
        setSingleScmMarketData([]);
        return;
      }

      // For edit/view mode with no recent updates: fetch market data
      setSingleScmMarketData([]);
      marketSelection();
    }
  }, [jobConfigId, refreshTrigger, searchParams]);

  const columns = [
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (text: string) => (
        <span className="font-semibold text-gray-800">{text}</span>
      ),
    },
    // {
    //   title: "Estimated Investment",
    //   dataIndex: "EstimatedInvestment",
    //   key: "EstimatedInvestment",
    //   render: (text: string) => (
    //     <span className="font-medium text-blue-700">{text}</span>
    //   ),
    // },
    // {
    //   title: "Duration",
    //   dataIndex: "duration",
    //   key: "duration",
    //   render: (text: string) => <span className="text-gray-800">{text}</span>,
    // },
    {
      title: "Power",
      dataIndex: "Power",
      key: "Power",
      render: (value: number) => (
        <span className="font-medium text-gray-800">{value.toFixed(0)}</span>
      ),
    },
    {
      title: "Expected Lift (%)",
      dataIndex: "abs_lift_in_perc",
      key: "abs_lift_in_perc",
      render: (text: string) => (
        <span className="font-medium text-gray-800">{text}</span>
      ),
    },
    {
      title: "Investment (%)",
      dataIndex: "PercentInvestment",
      key: "PercentInvestment",
      render: (text: string) => (
        <span className="font-medium text-gray-800">{text}</span>
      ),
    },
    {
      title: "Incremental Sales",
      dataIndex: "IncrementalSales",
      key: "IncrementalSales",
      render: (text: string) => (
        <span className="font-medium text-gray-800">{text}</span>
      ),
    },
    {
      title: "Action",
      key: "actions",
      render: (text: any) => (
        <Button
          type="primary"
          onClick={(e) => handleButtonClick(text?.location)}
          loading={buttonLoading}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 border-0 hover:from-blue-700 hover:to-indigo-700 shadow-sm hover:shadow-md transition-all duration-200"
        >
          View Control Market
        </Button>
      ),
    },
  ];

  return (
    <div className="min-h-screen p-3">
      {loadingLocations || singleScmMarketData.length === 0 ? (
        <EnhancedLoadingIndicator mode={mode} />
      ) : (
        <div className="w-full max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Test Markets
            </h2>
            <div className="flex flex-wrap gap-2">
              {testMarkets.map((ele, index) => {
                return (
                  <motion.button
                    key={ele}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="relative capitalize font-semibold px-2 py-2 rounded-xl
                      bg-gradient-to-r from-blue-600 to-indigo-600 text-white
                      shadow-lg hover:shadow-xl transition-all duration-300
                      border-0 overflow-hidden"
                    style={{
                      minWidth: "160px",
                    }}
                  >
                    <div className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-300"></div>
                    <div className="flex items-center justify-center gap-2 relative z-10">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span>{formatLocation(ele)}</span>
                    </div>
                  </motion.button>
                );
              })}
            </div>
            {selectedMarket && loadingLocations && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.3 }}
                className="mt-6 p-6 bg-white rounded-2xl border border-blue-100 shadow-md"
              >
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Spin size="small" />
                    <div className="absolute inset-0 bg-blue-400 blur-xl opacity-20 animate-pulse"></div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-base font-semibold text-gray-800">
                      Loading location data
                    </span>
                    <span className="text-sm text-gray-500">
                      Please wait while we fetch the information...
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100"
          >
            <MarketAnalysisDashboard
              allLocationData={locationData}
              data={singleScmMarketData}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
          >
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
              <h3 className="text-2xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Power Analysis Results
              </h3>
            </div>
            <Table
              dataSource={singleScmMarketData}
              columns={columns}
              pagination={false}
              bordered={false}
              className="custom-table"
              rowClassName="hover:bg-blue-50 transition-colors duration-200"
            />
          </motion.div>

          <Modal
            title={
              <div className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {selectedTitle || "Loading..."}
              </div>
            }
            open={isModalVisible}
            onCancel={handleModalClose}
            footer={null}
            width={900}
            className="custom-modal"
            centered
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
            <div
              style={{
                width: "100%",
                height: "450px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background:
                  "linear-gradient(to bottom right, #f8fafc, #f1f5f9)",
                borderRadius: "12px",
                padding: "20px",
              }}
            >



              {selectedTreatmentControlTitle &&
              geoLiftObsLabel?.length &&
              treatmentValue?.length ? (
                <LineChartWithTitle
                title = "Treatment vs Control Graph"
                 key={`control-treatment-${chartKey}`}
                  labels={geoLiftObsLabel as any}
                  dataSets={treatmentValue as any}
                  xAxisTitle="Time"
                  yAxisTitle="Outcome KPI"
                  highlightAreaFrom={plotDuration}
                  verticalLineAt={
                  plotDuration !== undefined
                  ? plotDuration
                  : undefined }
                  />) : (
                <div className="flex flex-col items-center gap-4">
                  <Spin size="large" />
                  <p className="text-gray-600 font-medium">
                    Loading chart data...
                  </p>
                 </div>
              )}
            </div>
            {selectedPowerTitle &&
              geoLiftPowerLabel?.length &&
              treatmentPowerValue?.length ? (
            <div
              style={{
                width: "100%",
                height: "450px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background:
                  "linear-gradient(to bottom right, #f8fafc, #f1f5f9)",
                borderRadius: "12px",
                padding: "20px",
              }}
            >

              {selectedPowerTitle &&
              geoLiftPowerLabel?.length &&
              treatmentPowerValue?.length ? (
                <LineChartWithTitle
                  title="Power Analysis Graph"
                  key={`power-${chartKey}`}
                  labels={geoLiftPowerLabel as any}
                  dataSets={ treatmentPowerValue as any}
                  xAxisTitle="EffectSize"
                  yAxisTitle="Power"
                  highlightAreaFrom={plotDuration}
                  verticalLineAt={plotDuration}
                />
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <Spin size="large" />
                  <p className="text-gray-600 font-medium">
                    Loading chart data...
                  </p>
                </div>
              )}
              </div>
              ) : (<div> </div>)}







            {isSales ? (

            <div
              style={{
                width: "100%",
                height: "450px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background:
                  "linear-gradient(to bottom right, #f8fafc, #f1f5f9)",
                borderRadius: "12px",
                padding: "20px",
              }}
            >

              {selectedPowerInvestmentTitle &&
              geoLiftPowerInvestmentLabel?.length &&
              treatmentPowerInvestmentValue?.length ? (
                <LineChartWithTitle
                  title="Invesment Incremental Sales Graph"
                  key={`power-investment`}
                  labels={geoLiftPowerInvestmentLabel as any}
                  dataSets={ treatmentPowerInvestmentValue as any}
                  xAxisTitle="Investment"
                  yAxisTitle="Incremental Sales"
                  highlightAreaFrom={plotDuration}
                  verticalLineAt={plotDuration}
                />
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <Spin size="large" />
                  <p className="text-gray-600 font-medium">
                    Loading chart data...
                  </p>
                </div>
              )
              }

            </div> ) : (<div></div>)

            }


            </div>

          </Modal>


          <style>{`
            .custom-table .ant-table {
              border-radius: 0 0 16px 16px;
            }
            .custom-table .ant-table-thead > tr > th {
              background: linear-gradient(to right, #eff6ff, #eef2ff);
              color: #1e40af;
              font-weight: 600;
              border-bottom: 2px solid #dbeafe;
              padding: 16px;
            }
            .custom-table .ant-table-tbody > tr > td {
              padding: 16px;
              border-bottom: 1px solid #f1f5f9;
            }
            .custom-table .ant-table-tbody > tr:last-child > td {
              border-bottom: none;
            }
            .custom-modal .ant-modal-content {
              border-radius: 20px;
              overflow: hidden;
              box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            }
            .custom-modal .ant-modal-header {
              background: linear-gradient(to right, #eff6ff, #eef2ff);
              border-bottom: 2px solid #dbeafe;
              padding: 20px 24px;
            }
            .custom-modal .ant-modal-body {
              padding: 24px;
            }

          `}</style>
        </div>
      )}
    </div>
  );
};

export default ViewTestAndControlMarket;
